import { addLocalDays } from "@normax-performance/shared-format";

export interface AnalyticsPeriodRange {
  from: string; // "YYYY-MM-DD"
  to: string; // "YYYY-MM-DD"
}

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** First calendar day of the month `date` falls in, local calendar fields — never UTC. */
function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** Whole previous calendar month relative to `date` — always valid (a month always has >= 28 days), used both as the default-period fallback and as the "Prethodni mesec" quick pick. `new Date(y, m-1, 1)`/`new Date(y, m, 0)` normalize year-end correctly (JS Date spec), the same reasoning `computePreviousPeriod` (backend, analytics-aggregation.logic.ts) relies on. */
function previousCompleteMonth(date: Date): AnalyticsPeriodRange {
  const firstOfPrevMonth = new Date(date.getFullYear(), date.getMonth() - 1, 1);
  const lastOfPrevMonth = new Date(date.getFullYear(), date.getMonth(), 0);
  return { from: isoDate(firstOfPrevMonth), to: isoDate(lastOfPrevMonth) };
}

/**
 * Default Analytics period — "current calendar month through yesterday"
 * (docs/NORMAX_PERFORMANCE_ANALYTICS_DASHBOARD_FORENSIC_PRODUCT_BRIEF.md §6,
 * this slice's approved product decision). Built entirely from local
 * calendar fields via `addLocalDays` — never UTC/millisecond arithmetic,
 * same reasoning as the Daily Entry yesterday-default fix.
 *
 * Honest fallback: when `today` is the 1st of a month, "this month through
 * yesterday" would mean [1st, last-day-of-PREVIOUS-month] — an inverted,
 * invalid range. Rather than clamping to an empty/single-day range, this
 * falls back to the previous COMPLETED calendar month in full — a real,
 * useful period instead of a degenerate one. Never returns `from > to`.
 */
export function computeDefaultAnalyticsPeriod(today: Date): AnalyticsPeriodRange {
  if (today.getDate() === 1) {
    return previousCompleteMonth(today);
  }
  const yesterday = addLocalDays(today, -1);
  return { from: isoDate(startOfMonth(today)), to: isoDate(yesterday) };
}

export type QuickPeriodKey = "yesterday" | "thisWeek" | "thisMonth" | "previousMonth";

/** Monday-start week, matching the Europe/Belgrade regional convention this product already targets. */
function startOfWeek(date: Date): Date {
  const daysSinceMonday = (date.getDay() + 6) % 7; // getDay(): 0=Sunday..6=Saturday
  return addLocalDays(date, -daysSinceMonday);
}

/**
 * The 4 period quick-picks (docs/.../FORENSIC_PRODUCT_BRIEF.md §6 +
 * this slice's period-control spec). Every branch is guaranteed
 * `from <= to` — "this week"/"this month" apply the same
 * through-yesterday + honest-fallback reasoning as the default period
 * (today's own data is not expected to be entered yet).
 */
export function computeQuickPeriod(key: QuickPeriodKey, today: Date): AnalyticsPeriodRange {
  switch (key) {
    case "yesterday": {
      const yesterday = addLocalDays(today, -1);
      return { from: isoDate(yesterday), to: isoDate(yesterday) };
    }
    case "thisWeek": {
      const monday = startOfWeek(today);
      const yesterday = addLocalDays(today, -1);
      if (monday > yesterday) {
        // today is Monday -> "this week through yesterday" is empty/inverted;
        // fall back to the previous complete week (last Mon–Sun).
        const previousMonday = addLocalDays(monday, -7);
        const previousSunday = addLocalDays(monday, -1);
        return { from: isoDate(previousMonday), to: isoDate(previousSunday) };
      }
      return { from: isoDate(monday), to: isoDate(yesterday) };
    }
    case "thisMonth":
      return computeDefaultAnalyticsPeriod(today);
    case "previousMonth":
      return previousCompleteMonth(today);
  }
}

/** Inclusive day count between two "YYYY-MM-DD" dates — used for the period control's human-readable recap ("13 dana"). */
export function computePeriodDayCount(from: string, to: string): number {
  const [fy, fm, fd] = from.split("-").map(Number);
  const [ty, tm, td] = to.split("-").map(Number);
  const fromDate = new Date(fy!, fm! - 1, fd!);
  const toDate = new Date(ty!, tm! - 1, td!);
  return Math.round((toDate.getTime() - fromDate.getTime()) / 86_400_000) + 1;
}
